import { useDeleteTour } from '@/api/tours';
import type { Tour } from '@/config/objects/tours';
import {
  Button,
  ConfirmDialog,
  EntityDetailShell,
  StatusPill,
  toast,
} from '@startupquickstart/frontend';
import {
  Activity,
  Building2,
  Languages,
  Landmark,
  Map,
  MapPin,
  Pencil,
  Route,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';
import { useMatch, useNavigate, useParams } from 'react-router-dom';

const tabs = [
  { label: 'Overview', path: 'overview', icon: Map },
  { label: 'Attractions', path: 'attractions', icon: MapPin },
  { label: 'Map', path: 'map', icon: Route },
  { label: 'Hotels', path: 'hotels', icon: Building2 },
  { label: 'Translations', path: 'translations', icon: Languages },
  { label: 'Health', path: 'health', icon: Activity },
] as const;

export function TourDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pendingDeleteTour, setPendingDeleteTour] = useState<Tour | null>(null);
  const translationMatch = useMatch('/tours/:id/translations/:translationId');
  const basePath = `/tours/${id}`;

  const deleteMutation = useDeleteTour();

  const handleDelete = () => {
    if (!pendingDeleteTour) return;
    const promise = deleteMutation.mutateAsync(pendingDeleteTour.id).then(() => {
      navigate('/tours');
    });
    toast.promise(promise, {
      loading: 'Deleting tour...',
      success: 'Tour deleted successfully',
      error: (error: Error) => error?.message || 'Failed to delete tour',
    });
  };

  return (
    <>
      <EntityDetailShell<Tour>
        resource="tours"
        queryKey="Tour"
        id={id}
        basePath={basePath}
        entityLabel="Tour"
        tabs={tabs}
        contextKey="tour"
        hero={{
          category: { label: 'Tour', icon: Map },
          title: (tour) => tour?.name || 'Tour',
          imageUrl: (tour) => tour?.featuredImage?.url ?? null,
          fallbackIcon: Map,
          imageAlt: (tour) => tour?.name || 'Tour',
          meta: (tour) => (
            <>
              {tour.greaterArea?.name && (
                <span className="flex items-center gap-1">
                  <Landmark className="h-3.5 w-3.5" />
                  {tour.greaterArea.name}
                </span>
              )}
              {tour.tourType?.name && (
                <span className="flex items-center gap-1">
                  <Map className="h-3.5 w-3.5" />
                  {tour.tourType.name}
                </span>
              )}
              {tour.tagline && <span className="italic">{tour.tagline}</span>}
            </>
          ),
          badges: (tour) => (tour.status ? <StatusPill status={tour.status} /> : null),
        }}
        breadcrumbs={(tour) => {
          const items: Array<{ label: string; href?: string; current?: boolean }> = [
            { label: 'Home', href: '/' },
            { label: 'Tours', href: '/tours' },
          ];
          if (translationMatch) {
            items.push({ label: tour?.name || 'Tour', href: `${basePath}/overview` });
            items.push({ label: 'Translations', href: `${basePath}/translations` });
            items.push({ label: 'Translation', current: true });
          } else {
            items.push({ label: tour?.name || 'Tour', current: true });
          }
          return items;
        }}
        headerActions={(tour) =>
          tour && !translationMatch ? (
            <>
              <Button variant="outline" onClick={() => navigate(`${basePath}/edit`)}>
                <Pencil className="h-4 w-4" />
                Edit
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  setPendingDeleteTour(tour);
                  setShowDeleteConfirm(true);
                }}
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            </>
          ) : null
        }
      />

      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete Tour"
        description={`Are you sure you want to delete "${pendingDeleteTour?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="destructive"
        loading={deleteMutation.isPending}
        onConfirm={handleDelete}
      />
    </>
  );
}
